import { useMemo, useState, useCallback, memo } from 'react';
import Loading from 'components/Loading';
import { useHoneGetUsers } from 'context/useAuthContext';
import { IconAddUser, IconDeleteOutline, IconEditUser, IconEmailOutline, IconLink } from 'components/Icons';
import KSButton from 'presentation/components/KSButton/KSButton';
import { HoneUserRoles, PayrollStatuses } from '@hone-automation/common';
import { Can } from '@casl/react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './BookkeperUsersForm.scss';
import {
  showToast,
  dismissToast,
  TOAST_DEFAULT,
  TOAST_SUCCESS,
  ONE_MINUTE,
  FIVE_SECONDS,
  TEN_SECONDS,
  TOAST_ERROR,
} from 'lib/utils';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useAuthContext } from 'context/useAuthContext';
import { RoleLabels } from '../../../../../constants';
import UserFormModal, { UserFormInputs } from 'presentation/components/UserFormModal/UserFormModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NameModal } from '../../../../components/Profile/NameModal';
import { useParams } from 'react-router-dom';
import { useLinkRollfiUser } from 'hooks/rollfi';
import { useUpdateLocationUser } from 'hooks/useUpdateLocationUser';
import { useAddLocationUser } from 'hooks/useAddLocationUser';

// Constants
type RoleChoice = (typeof HoneUserRoles)[keyof typeof HoneUserRoles];
const ROLE_CHOICES = Object.values(HoneUserRoles) as RoleChoice[];

// Types
interface UserRowProps {
  user: HoneLocationUser;
  hasRollfiConfig: boolean;
  isLinkPending: boolean;
  onRoleUpdate: (email: string, newRole: string) => void;
  onEmailOptOutChange: (email: string) => void;
  onEditUser: (user: HoneLocationUser) => void;
  onLinkToPayroll: (userId: string) => void;
  onPasswordReset: (email: string) => void;
  onRemoveUser: (email: string) => void;
}

interface TableHeaderProps {
  hasRollfiConfig: boolean;
}

// Memoized Components
const TableHeader = memo(function TableHeader({ hasRollfiConfig }: TableHeaderProps) {
  return (
    <thead>
      <tr className="bk-users-form-container__table_container-content-row">
        <th className="bk-users-form-container__table_container-content-row-cell-header align-left">Name</th>
        <th className="bk-users-form-container__table_container-content-row-cell-header align-left">Email</th>
        <th className="bk-users-form-container__table_container-content-row-cell-header align-left">Role</th>
        <th className="bk-users-form-container__table_container-content-row-cell-header">
          Email <br /> Opt Out
        </th>
        {hasRollfiConfig && (
          <th className="bk-users-form-container__table_container-content-row-cell-header">
            Payroll <br /> Status
          </th>
        )}
        <th className="bk-users-form-container__table_container-content-row-cell-header">
          Edit <br /> User
        </th>
        <th className="bk-users-form-container__table_container-content-row-cell-header">
          Send Password <br /> Reset Email
        </th>
        <th className="bk-users-form-container__table_container-content-row-cell-header">
          Remove <br /> User
        </th>
      </tr>
    </thead>
  );
});

const UserRow = memo(
  function UserRow({
    user,
    hasRollfiConfig,
    isLinkPending,
    onRoleUpdate,
    onEmailOptOutChange,
    onEditUser,
    onLinkToPayroll,
    onPasswordReset,
    onRemoveUser,
  }: UserRowProps) {
    const isRollfiEnabled =
      user?.userLocationConfig?.payrollStatus === PayrollStatuses.Active ||
      user?.userLocationConfig?.payrollStatus === PayrollStatuses.Onboarding;

    const handleRoleChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        onRoleUpdate(user.email, e.target.value);
      },
      [onRoleUpdate, user.email]
    );

    const handleEmailOptOut = useCallback(() => {
      onEmailOptOutChange(user.email);
    }, [onEmailOptOutChange, user.email]);

    const handleEdit = useCallback(() => {
      onEditUser(user);
    }, [onEditUser, user]);

    const handleLink = useCallback(() => {
      onLinkToPayroll(user.userId);
    }, [onLinkToPayroll, user.userId]);

    const handlePasswordReset = useCallback(() => {
      onPasswordReset(user.email);
    }, [onPasswordReset, user.email]);

    const handleRemove = useCallback(() => {
      onRemoveUser(user.email);
    }, [onRemoveUser, user.email]);

    return (
      <tr className="bk-users-form-container__table_container-content-row">
        <td className="bk-users-form-container__table_container-content-cell">{user.name}</td>
        <td className="bk-users-form-container__table_container-content-cell">{user.email}</td>
        <td className="bk-users-form-container__table_container-content-cell">
          <select name="role" value={user.role} onChange={handleRoleChange}>
            {ROLE_CHOICES.map(role => (
              <option value={role} key={role}>
                {RoleLabels[role as keyof typeof RoleLabels] ?? role}
              </option>
            ))}
          </select>
        </td>
        <td className="bk-users-form-container__table_container-content-cell text-center">
          <input type="checkbox" checked={user.emailOptOut} onChange={handleEmailOptOut} />
        </td>
        {hasRollfiConfig && (
          <td className="bk-users-form-container__table_container-content-cell text-center">
            {isRollfiEnabled ? (
              <span className="payroll-status-label">{user?.userLocationConfig?.payrollStatus}</span>
            ) : (
              <KSButton
                label="Link"
                color="inProgress"
                size="small"
                icon={<IconLink />}
                onClick={handleLink}
                disabled={isLinkPending}
              />
            )}
          </td>
        )}
        <td className="bk-users-form-container__table_container-content-cell text-center">
          <button className="button button_outline p-1" type="button" onClick={handleEdit}>
            <IconEditUser />
          </button>
        </td>
        <td className="bk-users-form-container__table_container-content-cell text-center">
          <button className="button button_outline p-1" type="button" onClick={handlePasswordReset}>
            <IconEmailOutline />
          </button>
        </td>
        <td className="bk-users-form-container__table_container-content-cell text-center">
          <button className="button BKForm-btn-alert p-1" type="button" onClick={handleRemove}>
            <IconDeleteOutline />
          </button>
        </td>
      </tr>
    );
  },
  (prev, next) =>
    prev.user === next.user &&
    prev.hasRollfiConfig === next.hasRollfiConfig &&
    prev.isLinkPending === next.isLinkPending
);

// Main Component
function BookkeeperUsersForm(): JSX.Element {
  const queryClient = useQueryClient();
  const { locationId } = useParams();
  const { user, currentLocationAbility, removeLocationUser, sendPasswordResetEmail } = useAuthContext();
  const { status: locationsStatus } = useLocationsStore();

  const { data: rawLocationUsers } = useHoneGetUsers(locationId, user ?? null);

  // Parse userLocationConfig JSON string for each user
  const allLocationUsers = useMemo(() => {
    if (!rawLocationUsers) return undefined;
    return rawLocationUsers.map(u => ({
      ...u,
      userLocationConfig: u.userLocationConfig ? JSON.parse(u.userLocationConfig as unknown as string) : undefined,
    }));
  }, [rawLocationUsers]);

  // Local state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingUser, setEditingUser] = useState<UserFormInputs | undefined>();
  const [linkIdLoading, setLinkIdLoading] = useState<string | null>(null);

  // Hooks
  const { mutate: linkRollfiUser, isPending: isLinkPending } = useLinkRollfiUser();
  const addUserMutation = useAddLocationUser();
  const updateUserMutation = useUpdateLocationUser();

  // Memoized values
  const hasRollfiConfig = useMemo(
    () => allLocationUsers?.some(u => u.userLocationConfig?.payrollStatus) ?? false,
    [allLocationUsers]
  );

  const usersMap = useMemo(() => {
    if (!allLocationUsers) return new Map<string, HoneLocationUser>();
    return new Map(allLocationUsers.map(u => [u.email, u]));
  }, [allLocationUsers]);

  const hasUsers = allLocationUsers && allLocationUsers.length > 0;
  const hasAbility = currentLocationAbility && Object.keys(currentLocationAbility).length > 0;

  // Mutations
  const removeUserMutation = useMutation({
    mutationFn: (email: string) => removeLocationUser(email),
    onMutate: () => showToast('Removing user from this location, please wait a bit.', TOAST_DEFAULT, ONE_MINUTE),
    onSuccess: (result: any, _variables, toastId) => {
      if (result === 'ok') {
        dismissToast(toastId);
        showToast('Successfully removed user.', TOAST_SUCCESS, FIVE_SECONDS);
      }
      queryClient.invalidateQueries({ queryKey: ['locationUsers'] });
    },
    onError: (error, _variables, toastId) => {
      toastId && dismissToast(toastId);
      showToast(`Error removing user: ${error}`, TOAST_ERROR, TEN_SECONDS);
    },
  });

  // Callbacks
  const handleLinkToPayroll = useCallback(
    (userId: string) => {
      setLinkIdLoading(userId);
      linkRollfiUser(userId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['locationsUsers'] });
          setLinkIdLoading(null);
        },
      });
    },
    [linkRollfiUser, queryClient]
  );

  const openAddModal = useCallback(() => {
    setModalMode('add');
    setEditingUser(undefined);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((userToEdit: HoneLocationUser) => {
    setModalMode('edit');
    setEditingUser({
      id: userToEdit.userId,
      name: userToEdit.name || '',
      email: userToEdit.email,
      role: userToEdit.role,
      description: userToEdit.description,
    });
    setIsNameModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const closeNameModal = useCallback(() => setIsNameModalOpen(false), []);

  const handleAddUser = useCallback(
    (userData: UserFormInputs) => {
      addUserMutation.mutate({
        userId: user?.uid || '',
        email: userData.email,
        password: userData.name,
        locationId: locationId || '',
        emailOptOut: false,
        role: userData.role || HoneUserRoles.Bookkeeper,
      });
    },
    [addUserMutation, user?.uid, locationId]
  );

  const handleEmailOptOutChange = useCallback(
    (email: string) => {
      const userToUpdate = usersMap.get(email);
      if (!userToUpdate) {
        showToast(`User not found: ${email}`, TOAST_ERROR, TEN_SECONDS);
        return;
      }
      updateUserMutation.mutate({
        userId: user?.uid || '',
        locationId: locationId || '',
        email,
        // @ts-ignore
        role: userToUpdate.role,
        emailOptOut: !userToUpdate.emailOptOut,
      });
    },
    [usersMap, updateUserMutation, user?.uid, locationId]
  );

  const handleRoleUpdate = useCallback(
    (email: string, newRole: string) => {
      const userToUpdate = usersMap.get(email);
      if (!userToUpdate) {
        showToast(`User not found: ${email}`, TOAST_ERROR, TEN_SECONDS);
        return;
      }
      updateUserMutation.mutate({
        userId: user?.uid || '',
        locationId: locationId || '',
        email,
        role: newRole,
        // @ts-ignore
        emailOptOut: userToUpdate.emailOptOut,
      });
    },
    [usersMap, updateUserMutation, user?.uid, locationId]
  );

  const handleRemoveLocationUser = useCallback(
    (email: string) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div className="alert-ui">
            <h3>Are you sure you want to remove &apos;{email}&apos; from this location?</h3>
            <button className="button BKForm-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button BKForm-btn"
              onClick={() => {
                onClose();
                removeUserMutation.mutate(email);
              }}
            >
              Remove User
            </button>
          </div>
        ),
      });
    },
    [removeUserMutation]
  );

  const handlePasswordReset = useCallback(
    (email: string) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div className="alert-ui">
            <h4 className="mb-8">Do you want to send a &apos;Set Password&apos; email to: {email}?</h4>
            <button className="button BKForm-btn-secondary mr-2" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button BKForm-btn"
              onClick={() => {
                onClose();
                const toastId = showToast("Sending 'Set Password' email", TOAST_DEFAULT, TEN_SECONDS);
                sendPasswordResetEmail({ email })
                  .then(() => {
                    dismissToast(toastId);
                    showToast('Successfully send email', TOAST_SUCCESS, TEN_SECONDS);
                  })
                  .catch((error: any) => {
                    dismissToast(toastId);
                    showToast(`Error sending password email, error=${error}`, TOAST_ERROR, TEN_SECONDS);
                  });
              }}
            >
              Send Email
            </button>
          </div>
        ),
      });
    },
    [sendPasswordResetEmail]
  );

  const handleNameModalSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['locationsUsers'] });
  }, [queryClient]);

  // Loading state
  if (locationsStatus === 'loading') {
    return (
      <div className="bookkeeper-tab-container">
        <div className="bk-users-form-container">
          <Loading loadingCircleClass="Loading-circle-small" />
        </div>
      </div>
    );
  }

  // No ability state
  if (!hasAbility) {
    return (
      <div className="bookkeeper-tab-container">
        <div className="bk-users-form-container" />
      </div>
    );
  }

  return (
    <div className="bookkeeper-tab-container">
      <div className="bk-users-form-container">
        <Can not I="read" a="User" ability={currentLocationAbility}>
          <div className="mt-4 mr-3">You do not have access to view/update this location&apos;s users.</div>
        </Can>

        <Can I="read" a="User" ability={currentLocationAbility}>
          {!hasUsers ? (
            <div className="mt-4 mr-3">No users found for this location.</div>
          ) : (
            <div className="bk-users-form-container__table_container">
              <div className="bk-users-form-container__header">
                <div className="bk-users-form-container__table_container__title">Users</div>
                <button className="button button-primary add-user-button" onClick={openAddModal}>
                  <IconAddUser /> Add User
                </button>
              </div>
              <div className="bk-users-form-container__table_wrapper">
                <table className="bk-users-form-container__table_container-content">
                  <TableHeader hasRollfiConfig={hasRollfiConfig} />
                  <tbody>
                    {allLocationUsers.map(locationUser => (
                      <UserRow
                        key={locationUser.email}
                        user={locationUser}
                        hasRollfiConfig={hasRollfiConfig}
                        isLinkPending={isLinkPending && linkIdLoading === locationUser.userId}
                        onRoleUpdate={handleRoleUpdate}
                        onEmailOptOutChange={handleEmailOptOutChange}
                        onEditUser={openEditModal}
                        onLinkToPayroll={handleLinkToPayroll}
                        onPasswordReset={handlePasswordReset}
                        onRemoveUser={handleRemoveLocationUser}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <UserFormModal
            isOpen={isModalOpen}
            closeModal={closeModal}
            onAddUser={modalMode === 'add' ? handleAddUser : undefined}
            editUser={editingUser}
            mode={modalMode}
          />

          {editingUser && (
            <NameModal
              isOpen={isNameModalOpen}
              onClose={closeNameModal}
              userId={editingUser.id}
              onSuccess={handleNameModalSuccess}
            />
          )}
        </Can>
      </div>
    </div>
  );
}

export default BookkeeperUsersForm;
